import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";

export default function DateRangePicker({
  onApply,
  onCancel,
  anchorRef,
  inline = false,
  align = "center", // "center" | "left" | "right"
  initialStartDate = null,
  initialEndDate = null,
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [hoverDate, setHoverDate] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [mounted, setMounted] = useState(false);

  const popupRef = useRef(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (initialStartDate) setStartDate(initialStartDate);
    if (initialEndDate) setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  // Tính toán vị trí dropdown (tương tự CustomSelect)
  const calcDropdownPos = useCallback(() => {
    if (inline) return;

    const anchor = anchorRef?.current;
    const dropdown = popupRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const gap = 6;
    const padding = 16; // padding từ edge của viewport

    // Lấy chiều cao và chiều rộng thực tế của dropdown
    const dropdownHeight = dropdown ? dropdown.offsetHeight : 400;
    const dropdownWidth = dropdown ? dropdown.offsetWidth : 620;

    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;

    // Quyết định đặt dropdown phía trên hay dưới
    const placeAbove =
      spaceBelow < dropdownHeight + gap && spaceAbove > spaceBelow;

    const top = placeAbove
      ? Math.max(gap, rect.top - dropdownHeight - gap)
      : rect.bottom + gap;

    // AUTO-ALIGN: Ưu tiên căn giữa, sau đó mới căn trái/phải
    let finalAlign = "center";
    let left;

    // Tính vị trí center
    const buttonCenter = rect.left + rect.width / 2;
    const centerLeft = buttonCenter - dropdownWidth / 2;
    const centerRight = buttonCenter + dropdownWidth / 2;

    // Kiểm tra nếu căn giữa có đủ không gian 2 bên
    if (centerLeft >= padding && centerRight <= vw - padding) {
      // Đủ chỗ -> căn giữa
      finalAlign = "center";
      left = centerLeft;
    }
    // Nếu không đủ chỗ căn giữa, thử căn trái (thẳng hàng với button)
    else if (
      rect.left >= padding &&
      rect.left + dropdownWidth <= vw - padding
    ) {
      // Còn đủ chỗ bên phải -> căn trái thẳng với button
      finalAlign = "left";
      left = rect.left;
    }
    // Nếu không đủ chỗ căn trái, thử căn phải (thẳng hàng với button)
    else if (
      rect.right >= dropdownWidth + padding &&
      rect.right <= vw - padding
    ) {
      // Còn đủ chỗ bên trái -> căn phải thẳng với button
      finalAlign = "right";
      left = rect.right - dropdownWidth;
    }
    // Fallback: giới hạn trong viewport (sát mép)
    else {
      finalAlign = "left";
      left = Math.max(
        padding,
        Math.min(vw - dropdownWidth - padding, rect.left),
      );
    }

    setDropdownPos({
      top,
      left,
      width: rect.width,
      placeAbove,
      align: finalAlign,
    });
  }, [inline, anchorRef, align]);

  // Update position khi mở dropdown
  useEffect(() => {
    if (inline) return;
    if (!anchorRef?.current) return;

    calcDropdownPos();

    const timer = setTimeout(() => {
      calcDropdownPos();
    }, 10);

    return () => clearTimeout(timer);
  }, [inline, calcDropdownPos]);

  // ResizeObserver để update position khi dropdown thay đổi kích thước
  useEffect(() => {
    if (inline || !popupRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calcDropdownPos();
    });

    resizeObserver.observe(popupRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [inline, calcDropdownPos]);

  // Update position khi scroll hoặc resize
  useEffect(() => {
    if (inline) return;

    const onResize = () => calcDropdownPos();
    const onScroll = () => calcDropdownPos();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [inline, calcDropdownPos]);

  // Click outside để đóng dropdown
  useEffect(() => {
    if (inline) return;

    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        !anchorRef?.current?.contains(e.target)
      ) {
        onCancel?.();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onCancel, anchorRef, inline]);

  const addMonths = (date, n) =>
    new Date(date.getFullYear(), date.getMonth() + n, 1);

  const formatMonthYear = (date) =>
    `Tháng ${date.getMonth() + 1}, ${date.getFullYear()}`;

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const isBetween = (d, s, e) => s && e && d > s && d < e;

  const handleSelect = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else if (date < startDate) {
      setStartDate(date);
    } else {
      setEndDate(date);
    }

    if (inline && onApply) {
      if (!startDate || (startDate && endDate)) {
        onApply({ startDate: date, endDate: null });
      } else {
        onApply({
          startDate: date < startDate ? date : startDate,
          endDate: date < startDate ? startDate : date,
        });
      }
    }
  };

  const renderMonth = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days = [];
    const startOffset = (firstDay.getDay() + 6) % 7;
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++)
      days.push(new Date(year, month, d));

    return (
      <div className={inline ? "" : "p-2"}>
        {!inline && (
          <div className="text-center font-semibold mb-2 whitespace-nowrap">
            {formatMonthYear(monthDate)}
          </div>
        )}

        <div className="grid grid-cols-7 gap-1 text-center text-[10px] sm:text-xs text-gray-400 mb-2">
          {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map((d) => (
            <div key={d} className="py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 text-center text-sm">
          {days.map((date, i) =>
            date ? (
              <button
                key={i}
                onClick={() => handleSelect(date)}
                onMouseEnter={() => setHoverDate(date)}
                className={`cursor-pointer w-10 h-[34px] flex items-center justify-center transition-all duration-150 rounded-[26px] sm:rounded-none outline-none focus:ring-2 focus:ring-[#0457C2]/40
                  ${isSameDay(date, startDate)
                    ? "bg-blue-gradient text-white font-semibold sm:rounded-l-lg rounded-[26px]"
                    : isSameDay(date, endDate)
                      ? "bg-blue-gradient text-white font-semibold sm:rounded-r-lg rounded-[26px]"
                      : (startDate &&
                        !endDate &&
                        hoverDate &&
                        isBetween(date, startDate, hoverDate)) ||
                        (startDate &&
                          endDate &&
                          isBetween(date, startDate, endDate))
                        ? "bg-[#d9f0ff]"
                        : "hover:bg-[#d9f0ff] hover:text-[#0457C2] rounded-[26px] sm:rounded-none font-medium leading-6"
                  }`}
              >
                {date.getDate()}
              </button>
            ) : (
              <div key={i}></div>
            ),
          )}
        </div>
      </div>
    );
  };

  const apply = () => {
    if (startDate && endDate && onApply) {
      onApply({ startDate, endDate });
    }
  };

  const content = (
    <div
      ref={!inline ? popupRef : null}
      onMouseDown={(e) => e.stopPropagation()}
      style={
        !inline
          ? {
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 9999,
          }
          : {}
      }
      className={`bg-white rounded-xl ${inline
        ? "p-4 border border-gray-200 rounded-2xl"
        : "border border-gray-200 shadow-xl p-3 min-w-[620px]"
        }`}
    >
      <div className="flex items-start mb-3 justify-between">
        <Button onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>
          <div className="w-8 h-8 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-200 hidden sm:flex justify-center items-center m-auto shrink-0 transition-colors">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </div>
          <div className="w-6 h-6 rounded-lg text-gray-600 hover:bg-gray-100 sm:hidden flex justify-center items-center m-auto shrink-0 transition-colors">
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </div>
        </Button>

        {inline ? (
          <span className="text-[15px] leading-6 font-medium text-gray-900">
            {formatMonthYear(currentMonth)}
          </span>
        ) : (
          <div className="flex gap-3 overflow-hidden">
            {renderMonth(currentMonth)}
            {renderMonth(addMonths(currentMonth, 1))}
          </div>
        )}

        <Button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <div className="w-8 h-8 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-200 hidden sm:flex justify-center items-center m-auto shrink-0 transition-colors">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
          <div className="w-6 h-6 rounded-lg text-gray-600 hover:bg-gray-100 sm:hidden flex justify-center items-center m-auto shrink-0 transition-colors">
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </Button>
      </div>

      {inline && renderMonth(currentMonth)}

      {!inline && (
        <div className="flex justify-end gap-3 mt-3 pt-3 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={onCancel}
          >
            Hủy bỏ
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={apply}
            disabled={!startDate || !endDate}
          >
            Áp dụng
          </Button>
        </div>
      )}
    </div>
  );

  if (inline) return content;

  return mounted ? createPortal(content, document.body) : null;
}
